
import React, { useState, useRef, useEffect } from 'react';
import ChatMessage from './components/ChatMessage';
import LeadDisplay from './components/LeadDisplay';
import { sendMessageStream, ai } from './services/geminiService';
import { SYSTEM_PROMPT } from './constants';

// Utility functions for audio
const decode = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

const encode = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const decodeAudioData = async (data, ctx, sampleRate, numChannels) => {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);
  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
};

const LogoIcon = ({ className = "w-8 h-8" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#A855F7" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
    </defs>
    <path d="M30 20 H15 V85 H85 V70 M85 45 V30" stroke="url(#logoGrad)" strokeWidth="12" />
    <path d="M35 65 L85 15 M85 15 H55 M85 15 V45" stroke="url(#logoGrad)" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SUGGESTIONS = [
  "I need a new website",
  "How can SEO help me?",
  "Custom App development",
  "Free strategy call"
];

const SquareTriggerChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'model', text: "Hi! 👋 Welcome to Square Trigger. We build high-performance websites and dominate SEO. How can I help your business grow?" }
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  const scrollRef = useRef(null);
  const liveSessionRef = useRef(null);
  const audioContextsRef = useRef(null);
  const nextStartTimeRef = useRef(0);
  const activeSourcesRef = useRef(new Set());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen, isLive]);

  const stopLiveMode = () => {
    if (liveSessionRef.current) {
      liveSessionRef.current.close();
      liveSessionRef.current = null;
    }
    activeSourcesRef.current.forEach(s => s.stop());
    activeSourcesRef.current.clear();
    setIsLive(false);
  };

  const startLiveMode = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsLive(true);
      setShowSuggestions(false);

      const inputCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      const outputCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      audioContextsRef.current = { input: inputCtx, output: outputCtx };

      const sessionPromise = ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        callbacks: {
          onopen: () => {
            const source = inputCtx.createMediaStreamSource(stream);
            const scriptProcessor = inputCtx.createScriptProcessor(4096, 1, 1);
            scriptProcessor.onaudioprocess = (e) => {
              const inputData = e.inputBuffer.getChannelData(0);
              const int16 = new Int16Array(inputData.length);
              for (let i = 0; i < inputData.length; i++) {
                int16[i] = inputData[i] * 32768;
              }
              const pcmBlob = {
                data: encode(new Uint8Array(int16.buffer)),
                mimeType: 'audio/pcm;rate=16000',
              };
              sessionPromise.then(session => session.sendRealtimeInput({ media: pcmBlob }));
            };
            source.connect(scriptProcessor);
            scriptProcessor.connect(inputCtx.destination);
          },
          onmessage: async (message) => {
            const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
            if (base64Audio) {
              const ctx = audioContextsRef.current.output;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, ctx.currentTime);
              const buffer = await decodeAudioData(decode(base64Audio), ctx, 24000, 1);
              const source = ctx.createBufferSource();
              source.buffer = buffer;
              source.connect(ctx.destination);
              source.onended = () => activeSourcesRef.current.delete(source);
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += buffer.duration;
              activeSourcesRef.current.add(source);
            }

            if (message.serverContent?.outputTranscription) {
              const text = message.serverContent.outputTranscription.text;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'model' && last.isStreaming) {
                  return [...prev.slice(0, -1), { role: 'model', text: last.text + text, isStreaming: true }];
                }
                return [...prev, { role: 'model', text, isStreaming: true }];
              });
            }

            if (message.serverContent?.inputTranscription) {
              const text = message.serverContent.inputTranscription.text;
              setMessages(prev => [...prev, { role: 'user', text }]);
            }

            if (message.serverContent?.turnComplete) {
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'model') return [...prev.slice(0, -1), { ...last, isStreaming: false }];
                return prev;
              });
            }

            if (message.serverContent?.interrupted) {
              activeSourcesRef.current.forEach(s => s.stop());
              activeSourcesRef.current.clear();
              nextStartTimeRef.current = 0;
            }
          },
          onclose: () => setIsLive(false),
          onerror: () => setIsLive(false),
        },
        config: {
          responseModalities: ["AUDIO"],
          systemInstruction: SYSTEM_PROMPT,
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Zephyr' } } },
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        }
      });
      liveSessionRef.current = await sessionPromise;
    } catch (err) {
      console.error("Live mode failed", err);
      setIsLive(false);
    }
  };

  const handleSend = async (customMsg) => {
    const userMsg = typeof customMsg === 'string' ? customMsg : input.trim();
    if (!userMsg || isProcessing) return;

    setInput('');
    setShowSuggestions(false);
    setIsProcessing(true);
    setMessages(prev => [...prev, { role: 'user', text: userMsg }, { role: 'model', text: '', isStreaming: true }]);
    
    try {
      await sendMessageStream(userMsg, (text) => {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMsg = newMessages[newMessages.length - 1];
          if (lastMsg.role === 'model') lastMsg.text = text;
          return newMessages;
        });
      });
    } catch (error) {
      setMessages(prev => [...prev.slice(0, -1), { role: 'model', text: "Service temporarily unavailable. Please verify your API key." }]);
    } finally {
      setIsProcessing(false);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg.role === 'model') lastMsg.isStreaming = false;
        return newMessages;
      });
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end font-sans">
      <div className={`mb-4 w-[90vw] sm:w-[420px] h-[650px] max-h-[85vh] bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col transition-all duration-500 origin-bottom-right ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}>
        
        {/* Header */}
        <div className="bg-white px-8 py-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center border relative">
              <LogoIcon className="w-9 h-9" />
              <span className={`absolute bottom-1 right-1 w-3 h-3 rounded-full border-2 border-white ${isLive ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`}></span>
            </div>
            <div>
              <h4 className="font-extrabold text-gray-900 text-lg">Square Trigger</h4>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{isLive ? 'Live Voice' : 'Online'}</p>
            </div>
          </div>
          <button onClick={() => { setIsOpen(false); stopLiveMode(); }} className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-grow overflow-y-auto px-8 py-6 space-y-5 bg-gray-50/20" ref={scrollRef}>
          {messages.map((msg, idx) => <ChatMessage key={idx} message={msg} />)}
          {showSuggestions && (
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => handleSend(s)} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all shadow-sm">
                  {s}
                </button>
              ))}
            </div>
          )}
          <LeadDisplay />
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 pt-4 bg-white">
          {isLive ? (
            <div className="flex flex-col items-center gap-4 py-4 bg-gray-50 rounded-3xl border border-gray-100">
              <div className="flex items-center gap-1.5 h-8">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-1 bg-blue-600 rounded-full animate-bounce" style={{ height: '100%', animationDelay: `${i * 0.1}s` }}></div>
                ))}
              </div>
              <button onClick={stopLiveMode} className="text-red-500 text-xs font-bold uppercase tracking-widest px-6 py-2 border border-red-100 rounded-full hover:bg-red-50">Stop Voice</button>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex items-center gap-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Message us..."
                className="flex-grow px-6 py-4 bg-gray-50 border border-gray-200 focus:outline-none focus:border-blue-500 rounded-2xl text-[15px]"
                disabled={isProcessing}
              />
              <button type="button" onClick={startLiveMode} className="p-4 bg-gradient-to-br from-[#A855F7] to-[#2563EB] text-white rounded-2xl shadow-lg hover:scale-105 active:scale-95 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Trigger */}
      <button onClick={() => setIsOpen(!isOpen)} className="w-18 h-18 bg-white rounded-[1.75rem] shadow-xl flex items-center justify-center transition-all hover:scale-110 active:scale-90 border border-gray-50">
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
        ) : (
          <LogoIcon className="w-11 h-11" />
        )}
      </button>
    </div>
  );
};

export default SquareTriggerChatbot;
